export function formatUnixTimestamp(unixTimestamp) {
  const date = new Date(unixTimestamp * 1000) // Convert seconds to milliseconds

  // Format options for US users
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }

  return new Intl.DateTimeFormat("en-US", options).format(date)
}

// Sort Utility
// export const sortData = (data, sortConfig) => {
//   if (sortConfig?.key) {
//     return [...data].sort((a, b) => {
//       const aValue = a[sortConfig.key]?.toString().toLowerCase() || ""
//       const bValue = b[sortConfig.key]?.toString().toLowerCase() || ""
//       if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1
//       if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1
//       return 0
//     })
//   }
//   return data
// }

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj)
}

export const sortData = (data, sortConfig) => {
  if (sortConfig?.key) {
    return [...data].sort((a, b) => {
      const aValue =
        getNestedValue(a, sortConfig.key)?.toString().toLowerCase() || ""
      const bValue =
        getNestedValue(b, sortConfig.key)?.toString().toLowerCase() || ""

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1
      return 0
    })
  }
  return data
}

// Pagination Utility
export const paginateData = (data, currentPage, itemsPerPage) => {
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = currentPage * itemsPerPage
  return data.slice(startIndex, endIndex)
}

// export function validateFaxNumber(faxNumber) {
//   const cleanedFaxNumber = faxNumber.replace(/[^\d]/g, "")

//   const regex = /^(1[2-9][0-9]{9}|[2-9][0-9]{9})$/

//   if (regex.test(cleanedFaxNumber)) {
//     return true
//   }

//   return false
// }

export function validateFaxNumber(faxNumber) {
  // Reject if it contains anything other than digits, +, or -
  if (!/^[0-9+-]+$/.test(faxNumber)) {
    return false
  }

  // Strip + and - for numeric validation
  const numericOnly = faxNumber.replace(/[+-]/g, "")

  // Must be 10 or 11 digits, same rules as before
  const regex = /^(1[2-9][0-9]{9}|[2-9][0-9]{9})$/

  return regex.test(numericOnly)
}

export function validateFaxNumberOnlyDigit(fax) {
  const faxNumber = fax.trim().replace(/-/g, "")
  // Rule 0: only digits allowed
  if (!/^\d+$/.test(faxNumber)) {
    return "Fax number must contain digits or `-` only"
  }

  const length = faxNumber.length
  const firstDigit = faxNumber.charAt(0)
  const secondDigit = faxNumber.charAt(1)

  if (length === 10 && firstDigit !== "1") {
    return "Invalid fax number! Please add country code `1`"
  }

  if (firstDigit === "1" && secondDigit === "1") {
    return "Invalid fax number"
  }

  if (length !== 11) {
    return "Fax number must have 11 digits"
  }

  // Rule 2: starts with "1" and has exactly 11 digits → correct
  if (firstDigit === "1" && length === 11) {
    return false // valid
  }

  // Rule 3: starts with > "1" and has exactly 11 digits → incorrect
  if (parseInt(firstDigit, 10) > 1 && length === 11) {
    return "Fax number cannot start with digit greater than 1 if it has 11 digits"
  }

  // Default: no error
  return false
}

export function prepareFinalFaxNumber(faxNumber) {
  const clearFaxNumber = faxNumber.trim().replace(/-/g, "")
  return clearFaxNumber.length === 10 ? `1${clearFaxNumber}` : clearFaxNumber
}

export const downloadPdfFromBase64 = (
  base64String,
  filename = "document.pdf"
) => {
  try {
    // Decode Base64 to binary
    const byteCharacters = atob(base64String)
    const byteNumbers = new Array(byteCharacters.length)
      .fill()
      .map((_, i) => byteCharacters.charCodeAt(i))
    const byteArray = new Uint8Array(byteNumbers)

    // Create a Blob and generate URL
    const blob = new Blob([byteArray], { type: "application/pdf" })
    const url = URL.createObjectURL(blob)

    // Create a temporary anchor to trigger download
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error("Error downloading PDF:", error)
  }
}

export const formatFaxNumber = (rawNumber) => {
  if (isEmpty(rawNumber, true)) {
    return ""
  }

  // if (!rawNumber || rawNumber.length !== 11 || !rawNumber.startsWith("1")) {
  //   return rawNumber // fallback for invalid input
  // }

  if (!rawNumber) {
    return rawNumber // fallback for invalid input
  }

  const countryCode = rawNumber.slice(0, 1)
  const areaCode = rawNumber.slice(1, 4)
  const prefix = rawNumber.slice(4, 7)
  const lineNumber = rawNumber.slice(7)

  return `${countryCode}-${areaCode}-${prefix}-${lineNumber}`
}

export function isEmpty(value, checkNullText = false) {
  // null or undefined
  if (value == null) return true

  if (value === "") return true

  if (checkNullText) {
    if (value === "null") return true
    if (value === "NULL") return true
    if (value.toLowerCase() === "unknown") return true
  }

  // Boolean or Number (including NaN) are never "empty"
  if (typeof value === "boolean" || typeof value === "number") return false

  // String: check length
  if (typeof value === "string") return value.length === 0

  // Array: check length
  if (Array.isArray(value)) return value.length === 0

  // Map or Set: check size
  if (value instanceof Map || value instanceof Set) return value.size === 0

  // Object: check own enumerable keys
  if (typeof value === "object") return Object.keys(value).length === 0

  // Fallback
  return false
}

export const customSelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    minWidth: 300,
    height: 41, // fixed height
    minHeight: 41,
    zIndex: 1000, // keep control above popup if needed
  }),
  valueContainer: (base) => ({
    ...base,
    justifyContent: "flex-start",
    paddingLeft: 8,
  }),
  singleValue: (base) => ({
    ...base,
    textAlign: "left",
    marginLeft: 0,
  }),
  placeholder: (base) => ({
    ...base,
    textAlign: "left",
    marginLeft: 0,
  }),
  menu: (base) => ({
    ...base,
    textAlign: "left",
    maxHeight: 200, // limit dropdown height
    overflowY: "auto", // enable scrolling
    zIndex: 2000, // ensure it renders above popup
    position: "absolute", // force it to overlay
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200, // limit dropdown height
    overflowY: "auto", // 👈 enables mouse wheel scroll
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000, // critical: ensures dropdown is above modals/popups
  }),
  option: (base, state) => ({
    ...base,
    textAlign: "left",
    backgroundColor: state.isSelected
      ? "#eee"
      : state.isFocused
      ? "#f5f5f5"
      : "white",
    color: "black",
  }),
}

export const displayNetIdEmail = (netId, email) => {
  return `${netId} ${!isEmpty(email, true) ? `[${email}]` : ``}`
}
